import type { RecordingCapabilitiesV1, SignalProfileV1, SignalNativeSeriesV1, SignalMinMaxSeriesV1 } from "@rekonas/domain";
import { presentationProfile } from "./presentationProfiles";
import { orientationModelForModality } from "./config";
import { channelVisibilityKey, trackVisibilityKey, defaultChannelVisible, type ViewerPreferences } from "./preferences";

export const DETAIL_WINDOWS = ["full", "15", "30", "60"] as const;
export type DetailWindowKey = typeof DETAIL_WINDOWS[number];
export type WindowVisibility = Partial<Record<DetailWindowKey, boolean>>;
export interface ChannelDisplayOverride {
  shortName?: string; longName?: string; colour?: string;
  scaleMode?: "auto" | "fixed"; span?: number;
  stageStyle?: "bands" | "hypnogram";
  positionRepresentation?: import("./presentationProfiles").PositionRepresentation;
  presentationStyle?: import("./presentationProfiles").PresentationStyle;
  batteryRepresentation?: "numeric" | "categories";
}
export interface DisplayChannel {
  key: string; trackKey: string; modalityId: string; channelId?: string;
  shortName: string; longName: string; rawName: string; modalityLabel?:string; unit?: string;
  sampleRate?: number; sampleCount?: number; startSeconds?: number; status?: string;
  defaultColour?:string; numeric: boolean; sleepStage: boolean; defaultVisible: boolean;
}
export function profileOrderKey(profile: SignalProfileV1) {
  return [profile.profile_id, profile.profile_version].map(encodeURIComponent).join("|");
}
/** Derive labels from the complete inventory, never the filtered visible subset. */
export function displayInventory(profile: SignalProfileV1, capabilities?: RecordingCapabilitiesV1): DisplayChannel[] {
  return [...profile.modalities].sort((a,b)=>a.order-b.order || a.id.localeCompare(b.id)).flatMap<DisplayChannel>(modality=>{
    if (modality.id === "position" || modality.kind === "media") return [];
    if (modality.kind === "quaternion") {
      const config=orientationModelForModality(modality.id), cap=capabilities?.modalities.find(c=>c.id===modality.id);
      // An expected source needs a verified component mapping, not a suggestive label.
      const components=[modality.components.w,modality.components.x,modality.components.y,modality.components.z];
      const mapped=cap && components.every(aliases=>cap.channels.filter(c=>aliases.includes(c.label)).length===1);
      const expectedGap=cap?.state === "temporarily_unavailable" && cap.channels.length===0 && components.every(aliases=>aliases.length>0);
      if(!config || !cap || !(mapped || expectedGap))return [];
      const id=`position:${modality.id}`, key=trackVisibilityKey(profile.profile_id,profile.profile_version,id);
      const name=`${config.id === "head"?"Head":"Torso"} position`;
      return [{key,trackKey:key,modalityId:id,shortName:name,longName:name,rawName:modality.id,modalityLabel:name,unit:"°",numeric:false,sleepStage:false,defaultVisible:true,status:`${cap.state} · computed display from native orientation; not a published scientific channel`}];
    }
    const cap=capabilities?.modalities.find(c=>c.id===modality.id);
    const trackKey=trackVisibilityKey(profile.profile_id,profile.profile_version,modality.id);
    const numeric=modality.kind === "scalar" || modality.kind === "multichannel";
    const channels=numeric && cap?.channels.length ? cap.channels : [undefined];
    return channels.map((channel,index)=>{
      const rawName=channel?.label || modality.label;
      const shortName=rawName.replace(/^[a-z]+\d+[_.\s-]+/i,"").replace(/[_-]+/g," ").replace(/([a-z])([0-9])/gi,"$1 $2").trim();
      return {key:channel?channelVisibilityKey(profile.profile_id,profile.profile_version,modality.id,channel.id):trackKey,
        trackKey,modalityId:modality.id,modalityLabel:modality.label,channelId:channel?.id,
        shortName:shortName || modality.label,longName:channel?`${modality.label} · ${channel.label}`:modality.label,rawName,
        defaultColour:numeric?(presentationProfile({modalityId:modality.id,unit:channel?.unit}).id === "light"?"#ffd400":TRACE_PALETTE[index%TRACE_PALETTE.length]):undefined,unit:channel?.unit,sampleRate:channel?.sample_rate_hz,sampleCount:channel?.sample_count,startSeconds:channel?.start_seconds,
        status:cap?`${cap.state}${cap.reason_code?` · ${cap.reason_code}`:""} · ${modality.origin}`:undefined,numeric,sleepStage:modality.id === "sleep_stage",defaultVisible:channel?defaultChannelVisible(modality.id,modality.label):true};
    });
  });
}
/** Missing new cells migrate the legacy effective parent AND child result to every window. */
export function channelVisible(p:ViewerPreferences, channel:DisplayChannel, window:DetailWindowKey):boolean {
  return p.windowVisibility[channel.key]?.[window] ??
    ((p.windowVisibility[channel.trackKey]?.[window] ?? p.trackVisibility[channel.trackKey] !== false) && (channel.channelId ? p.channelVisibility[channel.key] ?? channel.defaultVisible : true));
}
export function setVisibilityCells(p:ViewerPreferences, channels:DisplayChannel[], windows:readonly DetailWindowKey[], visible:boolean):ViewerPreferences {
  const windowVisibility={...p.windowVisibility},visibilityLabels={...p.visibilityLabels};
  for(const c of channels) {
    windowVisibility[c.key]={...windowVisibility[c.key],...Object.fromEntries(windows.map(w=>[w,visible]))};
    visibilityLabels[c.key]={modality:c.modalityLabel||c.longName,channel:c.rawName,...(c.defaultColour?{defaultColour:c.defaultColour}:{})};
  }
  return {...p,windowVisibility,visibilityLabels};
}
export function orderedInventory(inventory:DisplayChannel[], saved:readonly string[]=[]):DisplayChannel[] {
  const positions=new Map(saved.map((key,index)=>[key,index]));
  return inventory.map((c,index)=>({c,index})).sort((a,b)=>(positions.get(a.c.key)??saved.length+a.index)-(positions.get(b.c.key)??saved.length+b.index)).map(x=>x.c);
}
/** Replace present slots; retain temporarily absent keys and append genuinely new ones. */
export function mergeChannelOrder(saved:readonly string[], draft:readonly string[]):string[] {
  const present=new Set(draft);let index=0;
  const merged=saved.map(key=>present.has(key)?draft[index++]:key);
  return [...merged,...draft.slice(index)];
}
export const TRACE_PALETTE=["#15395f", "#139a8a", "#697f96", "#a57b16"];
export type NumericSeries=SignalNativeSeriesV1 | SignalMinMaxSeriesV1;
export function finiteVisibleRange(series:NumericSeries, interval:{start_seconds:number;end_seconds:number}):{minimum:number;maximum:number;centre:number}|null {
  let minimum=Infinity,maximum=-Infinity;
  const include=(value:number|null)=>{if(value!==null&&Number.isFinite(value)){minimum=Math.min(minimum,value);maximum=Math.max(maximum,value);}};
  if(series.representation === "native") series.values.forEach((v,i)=>{
    const t=series.start_seconds+i*series.sample_period_seconds;
    if(t>=interval.start_seconds&&t<interval.end_seconds)include(v);
  });
  else series.bins.forEach(b=>{if(b.start_seconds<interval.end_seconds&&b.end_seconds>interval.start_seconds){include(b.minimum);include(b.maximum);}});
  return Number.isFinite(minimum)?{minimum,maximum,centre:minimum+(maximum-minimum)/2}:null;
}
export function pooledVisibleRange(series:NumericSeries[],interval:{start_seconds:number;end_seconds:number}):ReturnType<typeof finiteVisibleRange> {
  const ranges=series.map(item=>finiteVisibleRange(item,interval)).filter(r=>r!==null);
  if(!ranges.length)return null;
  const minimum=Math.min(...ranges.map(r=>r.minimum)),maximum=Math.max(...ranges.map(r=>r.maximum));
  return {minimum,maximum,centre:minimum+(maximum-minimum)/2};
}
export function amplitudeBounds(range:ReturnType<typeof finiteVisibleRange>, override:ChannelDisplayOverride, centre?:number) {
  if(override.scaleMode === "fixed" && override.span && Number.isFinite(override.span) && override.span>0){
    const mid=centre??range?.centre??0;return {minimum:mid-override.span/2,maximum:mid+override.span/2};
  }
  if(!range)return {minimum:-1,maximum:1};
  if(range.minimum===range.maximum)return {minimum:range.centre-.5,maximum:range.centre+.5};
  return {minimum:range.minimum,maximum:range.maximum};
}
export function channelName(channel:DisplayChannel,p:ViewerPreferences):string {
  const mode=p.labelWidthMode;
  return (mode === "compact"?p.channelDisplay[channel.key]?.shortName:p.channelDisplay[channel.key]?.longName) || (mode === "compact"?channel.shortName:channel.longName);
}
